/* ============================================================================
   CM FAVOURITES — FINAL STABLE VERSION
   ---------------------------------------------------------------------------
   ✔ Fully compatible with PDP allocation script
   ✔ Works on collection grid, PDP & favourites page
   ✔ Handles Shopify AJAX partial reloads
   ✔ Updates UI instantly
   ✔ Syncs correctly with Azure API
   ✔ No duplicate SVG definitions (PROBLEM FIXED)
   ✔ No re-binding of click events (critical fix)
   ✔ Safe for infinite DOM changes & theme re-render
   ============================================================================ */

  (async () => {
      const apiBase = "https://clarinsfuncapp--uat.azurewebsites.net/api";
    
    


    // === SAFE FETCH WITH RETRIES =====================================
    // Retries Azure API calls (up to 3 times, exponential backoff)
    async function safeFetch(url, options = {}, retryCount = 0) {
      const MAX_RETRIES = 3;
      const TIMEOUT_MS  = 5000; // 5 sec timeout per request
    
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), TIMEOUT_MS);
    
      try {
        const res = await fetch(url, {
          ...options,
          signal: controller.signal
        });
      
        clearTimeout(id);
      
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res;
      
      } catch (err) {
        clearTimeout(id);
      
        if (retryCount >= MAX_RETRIES) {
          console.warn(`❌ Azure API failed after ${MAX_RETRIES} retries:`, err);
          throw err;
        }
      
        const delay = 300 * Math.pow(2, retryCount); // exponential backoff
        console.warn(`⚠ Azure API retry #${retryCount + 1} in ${delay}ms`);
      
        await new Promise(r => setTimeout(r, delay));
        return safeFetch(url, options, retryCount + 1);
      }
    }



  /* --------------------------------------------------------------------------
     CUSTOMER CONTEXT
     -------------------------------------------------------------------------- */
  const customerId = window.Shopify?.customerId || null;
  const payrollNumber = window.Shopify?.payrollNumber || null;
  const customerEmail = window.Shopify?.customerEmail || null;

  if (!payrollNumber) {
    console.warn("❌ Missing payroll number — Favourites disabled.");
    return;
  }

  /* --------------------------------------------------------------------------
     FAVOURITES STORE (LOCAL CACHE)
     -------------------------------------------------------------------------- */
  let favourites = new Set();
  let variantFavourites = new Set();

  try {
    const res = await safeFetch(`${apiBase}/FavouritesList/${payrollNumber}`, {
      headers: { "Cache-Control": "no-cache" }
    });
    const data = await res.json();

    if (data?.success && Array.isArray(data.favourites)) {
      favourites = new Set(data.favourites.map(f => String(f.ProductId)));
      variantFavourites = new Set(data.favourites.map(f => String(f.VariantId)));
      console.info(`⭐ Loaded ${favourites.size} favourites`);
    }

  } catch (err) {
    console.error("⚠ Could not load favourites:", err);
  }

  /* --------------------------------------------------------------------------
     HEART SVG TEMPLATE (ONE DEFINITION ONLY)
     -------------------------------------------------------------------------- */
  const heartSVG = `
    <svg class="heart-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 21s-6.9-4.8-9.5-9C-0.4 6.2 2.4 2 6.6 2
           c2.1 0 3.8 1.2 5.4 3 1.6-1.8 3.3-3 5.4-3
           4.2 0 7 4.2 4.1 10-2.6 4.2-9.5 9-9.5 9z"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      ></path>
    </svg>
  `;

  /* --------------------------------------------------------------------------
     CORE FUNCTION — Bind all favourite buttons
     -------------------------------------------------------------------------- */
  function initFavourites() {
    document.querySelectorAll(".favourite-toggle").forEach(btn => {

      const handle = btn.dataset.productHandle;
      const productId = String(btn.dataset.productId || "");
      const variantId = String(btn.dataset.variantId || productId);

      if (!productId) return;

      // Inject the heart icon if missing
      if (!btn.querySelector(".heart-icon")) btn.innerHTML = heartSVG;

      // PRE-SET UI STATE (based on Azure load)
      const isFav =
        favourites.has(productId) ||
        variantFavourites.has(variantId);

      btn.classList.toggle("active", isFav);
      btn.setAttribute("aria-pressed", isFav ? "true" : "false");

      // Prevent double binding
      if (btn.dataset.bound === "true") return;
      btn.dataset.bound = "true";

      /* ----------------------------------------------------------------------
         CLICK HANDLER — Toggle Favourite
         ---------------------------------------------------------------------- */
      btn.addEventListener("click", async (e) => {
        e.preventDefault();

        const willBeActive = !btn.classList.contains("active");

        // Immediate UI feedback
        btn.classList.toggle("active", willBeActive);
        btn.setAttribute("aria-pressed", willBeActive ? "true" : "false");

        try {
          const res = await safeFetch(`${apiBase}/FavouritesToggle`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              PayrollNumber: payrollNumber,
              Email: customerEmail,
              CustomerId: customerId,
              ProductHandle: handle,
              ProductId: productId,
              VariantId: variantId
            })
          });

          const data = await res.json();
          if (!data.success) throw new Error("API returned failure");

          // Update local sets for instant hydration-proof behaviour
          if (willBeActive) {
            favourites.add(productId);
            variantFavourites.add(variantId);
          } else {
            favourites.delete(productId);
            variantFavourites.delete(variantId);
          }

        } catch (err) {
          console.error("❌ Favourite toggle failed:", err);

          // Revert UI if API fails
          btn.classList.toggle("active", !willBeActive);
          btn.setAttribute("aria-pressed", !willBeActive ? "true" : "false");
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     INITIALISE
     -------------------------------------------------------------------------- */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFavourites);
  } else {
    initFavourites();
  }

  /* --------------------------------------------------------------------------
     SHOPIFY AJAX / PDP RERENDER PROTECTION
     -------------------------------------------------------------------------- */
  const observer = new MutationObserver(() => {
    initFavourites();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
